/**
 * LexDuo · F2 Ejecuciones · Panel Validación Pre-Ejecución (v59)
 * Cambios respecto a v57:
 *  - Auto-fill email procurador/cliente desde expediente
 *  - Editor enriquecido (TipTap) para el cuerpo del email
 *  - Botón "Editar plantillas" → modal PlantillasEmailEditor
 *  - Plantillas en catalán por defecto (migration_003)
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Mail, Send, ShieldAlert, CheckCircle2, AlertTriangle, Loader2,
  Upload, FileText, Trash2, User, Briefcase, Gavel, Lock, Unlock, Settings2,
} from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import RichTextEditor from '../RichTextEditor';
import PlantillasEmailEditor from './PlantillasEmailEditor';
import { obtenerToken } from '../../lib/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const headers = () => {
  const tok = obtenerToken();
  return tok ? { Authorization: `Bearer ${tok}` } : {};
};



// __V70_HILO__ — Componente Hilo de respuestas (Procurador / Cliente)
const HiloRespuestasV70 = ({ ejecucionId, tipo, refreshKey, onChanged }) => {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const cargar = React.useCallback(async () => {
    try {
      setLoading(true);
      const tok = obtenerToken();
      const h = tok ? { Authorization: `Bearer ${tok}` } : {};
      const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ejecuciones/${ejecucionId}/validacion/respuestas`, { headers: h });
      setItems((r.data?.items || []).filter(it => it.tipo === tipo));
    } catch { /* silencio */ }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, [ejecucionId, tipo]);
  React.useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [cargar, refreshKey]);
  const borrar = async (rid) => {
    if (!window.confirm('¿Eliminar esta respuesta del historial?')) return;
    try {
      const tok = obtenerToken();
      const h = tok ? { Authorization: `Bearer ${tok}` } : {};
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/ejecuciones/${ejecucionId}/validacion/respuestas/${rid}`, { headers: h });
      cargar(); onChanged?.();
    } catch (e) {
      alert(e?.response?.data?.detail || e.message);
    }
  };
  if (loading) return <div className="text-xs text-gray-400 italic mt-2">Cargando historial…</div>;
  if (!items.length) return <div className="text-xs text-gray-400 italic mt-2" data-testid={`hilo-${tipo}-vacio`}>Sin respuestas registradas aún.</div>;
  return (
    <div className="mt-3 border-l-2 border-violet-200 pl-3 space-y-2" data-testid={`hilo-${tipo}`}>
      <div className="text-xs font-semibold text-violet-700 uppercase tracking-wide">Historial · {items.length} respuesta{items.length>1?'s':''}</div>
      {items.map(it => (
        <div key={it.id} className="bg-white border border-gray-200 rounded p-2 text-xs flex justify-between gap-2" data-testid={`hilo-${tipo}-item-${it.id}`}>
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-700">
              {it.estado || '(sin estado)'}
              {it.importe != null && <span className="ml-2 text-emerald-700 font-mono">{Number(it.importe).toLocaleString('es-ES',{style:'currency',currency:'EUR'})}</span>}
              {it.fecha_recibida && <span className="ml-2 text-gray-500">· {it.fecha_recibida}</span>}
            </div>
            {it.concepto && <div className="text-gray-600 mt-0.5">Concepto: {it.concepto}</div>}
            {it.notas && <div className="text-gray-600 mt-0.5 whitespace-pre-wrap">{it.notas}</div>}
            <div className="text-[10px] text-gray-400 mt-1">{it.created_at ? new Date(it.created_at).toLocaleString('es-ES') : ''}</div>
          </div>
          <button onClick={() => borrar(it.id)} className="text-red-500 hover:text-red-700 text-xs" data-testid={`btn-borrar-respuesta-${it.id}`}>Eliminar</button>
        </div>
      ))}
    </div>
  );
};

const ESTADO_BADGE = {
  sin_consultar:        { color: 'bg-gray-200 text-gray-700', label: 'Sin consultar' },
  consultado:           { color: 'bg-amber-100 text-amber-800', label: 'Consultado · esperando respuesta' },
  sin_respuesta:        { color: 'bg-red-100 text-red-700', label: 'Sin respuesta' },
  sin_consignacion:     { color: 'bg-emerald-100 text-emerald-700', label: 'Sin consignación' },
  sin_pago:             { color: 'bg-emerald-100 text-emerald-700', label: 'Sin pago' },
  consignacion_parcial: { color: 'bg-orange-100 text-orange-700', label: 'Consignación parcial' },
  consignacion_total:   { color: 'bg-red-100 text-red-700', label: 'Consignación total' },
  pago_parcial:         { color: 'bg-orange-100 text-orange-700', label: 'Pago parcial' },
  pago_total:           { color: 'bg-red-100 text-red-700', label: 'Pago total' },
};

const ValidacionPreEjecucion = ({ ejecucion, onChanged }) => {
  const ejecucionId = ejecucion.id;
  const [loading, setLoading] = useState(true);
  const [v, setV] = useState(null);
  const [contactosDef, setContactosDef] = useState({ procurador: {}, cliente: {} });
  const [adjuntos, setAdjuntos] = useState([]);
  const [savingProc, setSavingProc] = useState(false);
  const [savingCli, setSavingCli] = useState(false);
  const [validating, setValidating] = useState(false);
  const [showPlantillas, setShowPlantillas] = useState(false);

  // Envío email
  const [emailModalOpen, setEmailModalOpen] = useState(null); // 'procurador' | 'cliente' | null
  const [emailTarget, setEmailTarget] = useState('');
  const [emailTargetName, setEmailTargetName] = useState('');
  const [emailPreview, setEmailPreview] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Respuesta procurador (form)
  const [procEstado, setProcEstado] = useState('');
  const [procImporte, setProcImporte] = useState('');
  const [procNotas, setProcNotas] = useState('');

  // Respuesta cliente (form)
  const [cliEstado, setCliEstado] = useState('');
  const [cliImporte, setCliImporte] = useState('');
  const [cliFecha, setCliFecha] = useState('');
  const [cliConcepto, setCliConcepto] = useState('');
  const [cliNotas, setCliNotas] = useState('');

  // Validación final
  const [resultadoFinal, setResultadoFinal] = useState('');
  const [notasFinal, setNotasFinal] = useState('');
  const [hiloRefresh, setHiloRefresh] = useState(0);

  const cargar = async () => {
    setLoading(true);
    try {
      const [rv, ra, rc] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/validacion`, { headers: headers() }),
        axios.get(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/adjuntos`, { headers: headers() }),
        axios.get(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/contactos-defecto`, { headers: headers() }),
      ]);
      setV(rv.data);
      setAdjuntos(ra.data?.items || []);
      setContactosDef(rc.data || { procurador: {}, cliente: {} });
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [ejecucionId]);

  const abrirEnvioEmail = async (tipo) => {
    setEmailModalOpen(tipo);
    // Pre-fill con email del expediente (o el que ya consta en validación)
    const fromValid = tipo === 'procurador' ? v?.procurador?.email : v?.cliente?.email;
    const fromExp = tipo === 'procurador' ? contactosDef?.procurador?.email : contactosDef?.cliente?.email;
    const nameExp = tipo === 'procurador' ? contactosDef?.procurador?.nombre : contactosDef?.cliente?.nombre;
    setEmailTarget(fromValid || fromExp || '');
    setEmailTargetName(nameExp || '');
    setEmailPreview(null);
    try {
      const codigo = tipo === 'procurador' ? 'consulta_procurador' : 'consulta_cliente';
      const r = await axios.get(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/email-preview/${codigo}`, { headers: headers() });
      setEmailPreview(r.data);
    } catch (e) {
      toast({ title: 'Error preview', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const enviarEmail = async () => {
    if (!emailTarget.trim() || !emailTarget.includes('@')) {
      toast({ title: 'Email inválido', variant: 'destructive' }); return;
    }
    setSendingEmail(true);
    try {
      const codigo = emailModalOpen === 'procurador' ? 'consulta_procurador' : 'consulta_cliente';
      await axios.post(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/enviar-email`, {
        plantilla_codigo: codigo,
        destinatario_tipo: emailModalOpen,
        destinatario_email: emailTarget.trim(),
        asunto_override: emailPreview?.asunto,
        cuerpo_override: emailPreview?.cuerpo,
      }, { headers: headers() });
      toast({ title: 'Email enviado', description: `A ${emailTarget}` });
      setEmailModalOpen(null);
      await cargar();
      setHiloRefresh(x => x + 1);
      onChanged?.();
    } catch (e) {
      toast({ title: 'Error envío', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSendingEmail(false); }
  };

  const guardarRespuestaProcurador = async () => {
    if (!procEstado) { toast({ title: 'Selecciona el estado', variant: 'destructive' }); return; }
    setSavingProc(true);
    try {
      await axios.post(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/validacion/respuesta-procurador`, {
        estado: procEstado,
        importe_consignado: parseFloat(procImporte) || null,
        notas: procNotas || null,
      }, { headers: headers() });
      toast({ title: 'Respuesta del procurador registrada' });
      setProcEstado(''); setProcImporte(''); setProcNotas('');
      await cargar();
      setHiloRefresh(x => x + 1);
      onChanged?.();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSavingProc(false); }
  };

  const guardarRespuestaCliente = async () => {
    if (!cliEstado) { toast({ title: 'Selecciona el estado', variant: 'destructive' }); return; }
    setSavingCli(true);
    try {
      await axios.post(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/validacion/respuesta-cliente`, {
        estado: cliEstado,
        importe_pagado: parseFloat(cliImporte) || null,
        fecha_pago: cliFecha || null,
        concepto: cliConcepto || null,
        notas: cliNotas || null,
      }, { headers: headers() });
      toast({ title: 'Respuesta del cliente registrada' });
      setCliEstado(''); setCliImporte(''); setCliFecha(''); setCliConcepto(''); setCliNotas('');
      await cargar();
      setHiloRefresh(x => x + 1);
      onChanged?.();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSavingCli(false); }
  };

  const validarFinal = async () => {
    if (!resultadoFinal) { toast({ title: 'Selecciona resultado', variant: 'destructive' }); return; }
    setValidating(true);
    try {
      const r = await axios.post(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/validacion/validar`, {
        resultado: resultadoFinal,
        notas: notasFinal || null,
      }, { headers: headers() });
      toast({
        title: 'Validación registrada',
        description: r.data?.auto_transicion ? `Estado: ${r.data.auto_transicion[0]} → ${r.data.auto_transicion[1]}` : ''
      });
      setResultadoFinal(''); setNotasFinal('');
      await cargar();
      setHiloRefresh(x => x + 1);
      onChanged?.();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setValidating(false); }
  };

  const subirAdjunto = async (tipo, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('tipo', tipo);
    fd.append('file', file);
    try {
      await axios.post(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/adjuntos`, fd, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' }
      });
      toast({ title: 'Archivo subido' });
      await cargar();
    } catch (e) {
      toast({ title: 'Error upload', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const borrarAdjunto = async (adjId) => {
    if (!window.confirm('¿Borrar este adjunto?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/adjuntos/${adjId}`, { headers: headers() });
      await cargar();
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const downloadAdjunto = (adjId) => {
    window.open(`${BACKEND_URL}/api/ejecuciones/${ejecucionId}/adjuntos/${adjId}/download`, '_blank');
  };

  if (loading || !v) {
    return <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-violet-700" /></div>;
  }

  const badge = (estado) => {
    const c = ESTADO_BADGE[estado] || { color: 'bg-gray-100', label: estado };
    return <Badge className={`${c.color} hover:${c.color}`}>{c.label}</Badge>;
  };

  const adjuntosPorTipo = (tipo) => adjuntos.filter(a => a.tipo === tipo);

  return (
    <div className="space-y-5" data-testid="validacion-preejecucion">

      {/* Banner de bloqueo + botón Editar plantillas */}
      <div className="flex items-start gap-3">
        <Card className={`flex-1 ${v.puede_pasar_apta ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`}>
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              {v.puede_pasar_apta
                ? <CheckCircle2 className="w-7 h-7 text-emerald-600 flex-shrink-0" />
                : <Lock className="w-7 h-7 text-red-600 flex-shrink-0" />}
              <div className="flex-1">
                <div className="font-semibold text-base">
                  {v.puede_pasar_apta ? 'Validació COMPLETA — Execució apta per a demanda' : 'Execució BLOQUEJADA — Validació pendent'}
                </div>
                {!v.puede_pasar_apta && v.motivo_bloqueo && (
                  <div className="text-sm text-red-700 mt-1">Motiu: {v.motivo_bloqueo}</div>
                )}
                <div className="text-xs text-gray-600 mt-2">
                  No es pot iniciar demanda executiva sense verificació documentada (procurador + client + advocat).
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <Button variant="outline" size="sm" onClick={() => setShowPlantillas(true)} data-testid="btn-editar-plantillas" className="mt-1">
          <Settings2 className="w-4 h-4 mr-1.5" /> Plantilles
        </Button>
      </div>

      {/* Resolución a ejecutar */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <FileText className="w-5 h-5 text-violet-700" /> Resolució a executar
          </CardTitle>
          <CardDescription>Pugeu la sentència / acte / decret ferm que es vol executar.</CardDescription>
        </CardHeader>
        <CardContent>
          <input type="file" id="upload-resolucion" hidden accept="application/pdf"
                 onChange={(e) => subirAdjunto('resolucion', e.target.files?.[0])} />
          <Button variant="outline" onClick={() => document.getElementById('upload-resolucion').click()} data-testid="btn-upload-resolucion">
            <Upload className="w-4 h-4 mr-2" /> Pujar resolució (PDF)
          </Button>
          {adjuntosPorTipo('resolucion').length > 0 && (
            <div className="mt-3 space-y-1.5">
              {adjuntosPorTipo('resolucion').map(a => (
                <div key={a.id} className="flex items-center justify-between bg-violet-50 px-3 py-1.5 rounded text-sm" data-testid={`adj-${a.id}`}>
                  <button onClick={() => downloadAdjunto(a.id)} className="text-left flex-1 truncate hover:underline">
                    📄 {a.nombre_archivo} <span className="text-xs text-gray-500">({Math.round(a.tamano/1024)} KB)</span>
                  </button>
                  <Button variant="ghost" size="sm" onClick={() => borrarAdjunto(a.id)}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Grid 2 columnas: procurador / cliente */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* PROCURADOR */}
        <Card className="border-blue-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Briefcase className="w-5 h-5 text-blue-700" /> Procurador</CardTitle>
            <CardDescription>
              Verificació de consignació judicial
              {contactosDef?.procurador?.nombre && (
                <span className="block text-[11px] mt-1 text-blue-700">
                  Per defecte: <b>{contactosDef.procurador.nombre}</b>
                  {contactosDef.procurador.email ? ` · ${contactosDef.procurador.email}` : ' · (sense email registrat)'}
                </span>
              )}
            </CardDescription>
            <div className="pt-1">{badge(v.procurador.estado)}</div>
          </CardHeader>
          <CardContent className="space-y-3">
            {v.procurador.consultado_at && (
              <div className="text-xs text-gray-500">
                Consulta enviada: {new Date(v.procurador.consultado_at).toLocaleString('ca-ES')}
                {v.procurador.email && <> · a <b>{v.procurador.email}</b></>}
              </div>
            )}
            {v.procurador.respuesta_at && (
              <div className="text-xs text-gray-500">Resposta: {new Date(v.procurador.respuesta_at).toLocaleString('ca-ES')}</div>
            )}

            <Button onClick={() => abrirEnvioEmail('procurador')} size="sm" variant="outline" className="w-full" data-testid="btn-enviar-email-procurador">
              <Mail className="w-4 h-4 mr-2" /> {v.procurador.consultado_at ? 'Reenviar consulta' : 'Enviar consulta'}
            </Button>

            <div className="border-t pt-3 space-y-2">
              <Label className="text-xs">Registrar resposta del procurador</Label>
              <Select value={procEstado} onValueChange={setProcEstado}>
                <SelectTrigger data-testid="select-resp-procurador"><SelectValue placeholder="Estat de la resposta" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sin_consignacion">Sense consignació</SelectItem>
                  <SelectItem value="consignacion_parcial">Consignació parcial</SelectItem>
                  <SelectItem value="consignacion_total">Consignació total</SelectItem>
                  <SelectItem value="sin_respuesta">Sense resposta després del termini</SelectItem>
                </SelectContent>
              </Select>
              {(procEstado === 'consignacion_parcial' || procEstado === 'consignacion_total') && (
                <Input type="number" step="0.01" value={procImporte} onChange={(e) => setProcImporte(e.target.value)} placeholder="Import consignat (€)" data-testid="input-importe-procurador" />
              )}
              <Textarea value={procNotas} onChange={(e) => setProcNotas(e.target.value)} rows={2} placeholder="Notes" />
              <HiloRespuestasV70 ejecucionId={ejecucionId} tipo="procurador" refreshKey={hiloRefresh} onChanged={() => onChanged?.()} />
              <input type="file" id="upload-resp-proc" hidden onChange={(e) => subirAdjunto('respuesta_procurador', e.target.files?.[0])} />
              <Button variant="outline" size="sm" onClick={() => document.getElementById('upload-resp-proc').click()}>
                <Upload className="w-3.5 h-3.5 mr-1" /> Adjuntar email/justificant
              </Button>
              <Button onClick={guardarRespuestaProcurador} disabled={savingProc} size="sm" className="w-full bg-blue-700 hover:bg-blue-800" data-testid="btn-guardar-resp-procurador">
                {savingProc ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Desar resposta'}
              </Button>
              {adjuntosPorTipo('respuesta_procurador').map(a => (
                <div key={a.id} className="flex items-center justify-between bg-blue-50 px-2 py-1 rounded text-xs">
                  <button onClick={() => downloadAdjunto(a.id)} className="truncate hover:underline">📎 {a.nombre_archivo}</button>
                  <Button variant="ghost" size="sm" onClick={() => borrarAdjunto(a.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* CLIENTE */}
        <Card className="border-emerald-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><User className="w-5 h-5 text-emerald-700" /> Client</CardTitle>
            <CardDescription>
              Verificació de pagaments directes
              {contactosDef?.cliente?.nombre && (
                <span className="block text-[11px] mt-1 text-emerald-700">
                  Per defecte: <b>{contactosDef.cliente.nombre}</b>
                  {contactosDef.cliente.email ? ` · ${contactosDef.cliente.email}` : ' · (sense email registrat)'}
                </span>
              )}
            </CardDescription>
            <div className="pt-1">{badge(v.cliente.estado)}</div>
          </CardHeader>
          <CardContent className="space-y-3">
            {v.cliente.consultado_at && (
              <div className="text-xs text-gray-500">
                Consulta enviada: {new Date(v.cliente.consultado_at).toLocaleString('ca-ES')}
                {v.cliente.email && <> · a <b>{v.cliente.email}</b></>}
              </div>
            )}
            {v.cliente.respuesta_at && (
              <div className="text-xs text-gray-500">Resposta: {new Date(v.cliente.respuesta_at).toLocaleString('ca-ES')}</div>
            )}

            <Button onClick={() => abrirEnvioEmail('cliente')} size="sm" variant="outline" className="w-full" data-testid="btn-enviar-email-cliente">
              <Mail className="w-4 h-4 mr-2" /> {v.cliente.consultado_at ? 'Reenviar consulta' : 'Enviar consulta'}
            </Button>

            <div className="border-t pt-3 space-y-2">
              <Label className="text-xs">Registrar resposta del client</Label>
              <Select value={cliEstado} onValueChange={setCliEstado}>
                <SelectTrigger data-testid="select-resp-cliente"><SelectValue placeholder="Estat de la resposta" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sin_pago">Sense pagament directe</SelectItem>
                  <SelectItem value="pago_parcial">Pagament parcial</SelectItem>
                  <SelectItem value="pago_total">Pagament total</SelectItem>
                  <SelectItem value="sin_respuesta">Sense resposta després del termini</SelectItem>
                </SelectContent>
              </Select>
              {(cliEstado === 'pago_parcial' || cliEstado === 'pago_total') && (
                <>
                  <Input type="number" step="0.01" value={cliImporte} onChange={(e) => setCliImporte(e.target.value)} placeholder="Import pagat (€)" data-testid="input-importe-cliente" />
                  <Input type="date" value={cliFecha} onChange={(e) => setCliFecha(e.target.value)} placeholder="Data del pagament" />
                  <Input value={cliConcepto} onChange={(e) => setCliConcepto(e.target.value)} placeholder="Concepte del pagament" />
                </>
              )}
              <Textarea value={cliNotas} onChange={(e) => setCliNotas(e.target.value)} rows={2} placeholder="Notes" />
              <HiloRespuestasV70 ejecucionId={ejecucionId} tipo="cliente" refreshKey={hiloRefresh} onChanged={() => onChanged?.()} />
              <input type="file" id="upload-resp-cli" hidden onChange={(e) => subirAdjunto('respuesta_cliente', e.target.files?.[0])} />
              <Button variant="outline" size="sm" onClick={() => document.getElementById('upload-resp-cli').click()}>
                <Upload className="w-3.5 h-3.5 mr-1" /> Adjuntar email/justificant bancari
              </Button>
              <Button onClick={guardarRespuestaCliente} disabled={savingCli} size="sm" className="w-full bg-emerald-700 hover:bg-emerald-800" data-testid="btn-guardar-resp-cliente">
                {savingCli ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Desar resposta'}
              </Button>
              {adjuntosPorTipo('respuesta_cliente').map(a => (
                <div key={a.id} className="flex items-center justify-between bg-emerald-50 px-2 py-1 rounded text-xs">
                  <button onClick={() => downloadAdjunto(a.id)} className="truncate hover:underline">📎 {a.nombre_archivo}</button>
                  <Button variant="ghost" size="sm" onClick={() => borrarAdjunto(a.id)}><Trash2 className="w-3 h-3 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Validación final del abogado */}
      <Card className="border-violet-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Gavel className="w-5 h-5 text-violet-700" /> Validació final de l&apos;advocat responsable</CardTitle>
          <CardDescription>
            Després de revisar les respostes de procurador i client, l&apos;advocat responsable ha de validar formalment.
            Aquesta acció pot desbloquejar el pas a &quot;Apta demanda&quot; o marcar l&apos;execució com satisfeta.
          </CardDescription>
          {v.validacion.resultado && (
            <div className="pt-1">
              <Badge className="bg-violet-100 text-violet-800">Validada: {v.validacion.resultado}</Badge>
              {v.validacion.validado_at && <span className="text-xs text-gray-500 ml-2">{new Date(v.validacion.validado_at).toLocaleString('ca-ES')}</span>}
            </div>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={resultadoFinal} onValueChange={setResultadoFinal}>
            <SelectTrigger data-testid="select-resultado-final"><SelectValue placeholder="Resultat de la validació..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="apto">✓ Apte · procedir a demanda executiva</SelectItem>
              <SelectItem value="recalculo_parcial">⚠ Recàlcul necessari · procedir amb saldo recalculat</SelectItem>
              <SelectItem value="bloqueado_pago_total">✗ Bloquejat · pagament total rebut — marcar com satisfeta</SelectItem>
              <SelectItem value="forzado">⚡ Forçar continuació (assumint risc)</SelectItem>
            </SelectContent>
          </Select>
          <Textarea value={notasFinal} onChange={(e) => setNotasFinal(e.target.value)} rows={3} placeholder="Notes / justificació de la validació final" data-testid="input-notas-validacion-final" />
          <Button onClick={validarFinal} disabled={validating || !resultadoFinal} className="bg-violet-700 hover:bg-violet-800" data-testid="btn-validar-final">
            {validating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Validant…</> : <><Unlock className="w-4 h-4 mr-2" /> Validar i aplicar transició</>}
          </Button>
        </CardContent>
      </Card>

      {/* Modal envío email — con RichTextEditor */}
      {emailModalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEmailModalOpen(null)}>
          <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="modal-envio-email">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                <Mail className="w-5 h-5" /> Enviar consulta · {emailModalOpen === 'procurador' ? 'Procurador' : 'Client'}
                {emailTargetName && <span className="text-sm font-normal text-gray-500">— {emailTargetName}</span>}
              </h3>
            </div>
            <div className="p-4 space-y-3">
              <div>
                <Label className="text-xs">Email destinatari</Label>
                <Input type="email" value={emailTarget} onChange={(e) => setEmailTarget(e.target.value)} placeholder="exemple@domini.com" data-testid="input-email-destinatario" />
                <div className="text-[11px] text-gray-500 mt-1">
                  Auto-completat des del contacte de l&apos;expedient. Podeu editar-lo si cal.
                </div>
              </div>
              {emailPreview && (
                <>
                  <div>
                    <Label className="text-xs">Assumpte</Label>
                    <Input value={emailPreview.asunto} onChange={(e) => setEmailPreview({ ...emailPreview, asunto: e.target.value })} data-testid="input-asunto-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Cos del missatge</Label>
                    <div className="border rounded-md">
                      <RichTextEditor
                        value={emailPreview.cuerpo}
                        onChange={(html) => setEmailPreview({ ...emailPreview, cuerpo: html })}
                        placeholder="Cos del correu..."
                      />
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="p-4 border-t flex justify-between gap-2">
              <Button variant="ghost" size="sm" onClick={() => { setEmailModalOpen(null); setTimeout(() => setShowPlantillas(true), 100); }}>
                <Settings2 className="w-4 h-4 mr-1" /> Editar plantilla
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEmailModalOpen(null)}>Cancel·lar</Button>
                <Button onClick={enviarEmail} disabled={sendingEmail || !emailTarget} className="bg-violet-700 hover:bg-violet-800" data-testid="btn-confirmar-envio-email">
                  {sendingEmail ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviant…</> : <><Send className="w-4 h-4 mr-2" /> Enviar</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <PlantillasEmailEditor open={showPlantillas} onClose={() => setShowPlantillas(false)} />
    </div>
  );
};

export default ValidacionPreEjecucion;
